import type { VoidFunctionComponent } from 'react';

import { FormGroup, LabeledCheckbox } from '../../generic/Forms';

import Section from './SettingsPageSection';

export type BaseLayerExclusionProps = {
  excludeLicensingIssues: boolean;
  onToggleExcludeLicensingIssues: (isChecked: boolean) => void;
  excludeSecurityIssues: boolean;
  onToggleExcludeSecurityIssues: (isChecked: boolean) => void;
  excludeQualityIssues: boolean;
  onToggleExcludeQualityIssues: (isChecked: boolean) => void;
  isDisabled?: boolean;
};

/**
 * BaseLayerExclusionFields renders the "Base layer" subsection shared by the
 * organization-level and per-project container settings: a heading, an
 * explanatory paragraph, and the three per-category exclusion checkboxes.
 *
 * It renders the contents intended to sit inside a <Section.Right>; the caller
 * owns the surrounding <Section> and its left column (which differs between the
 * org page, with save/propagate buttons, and the project page).
 */
export const BaseLayerExclusionFields: VoidFunctionComponent<BaseLayerExclusionProps> = ({
  excludeLicensingIssues,
  onToggleExcludeLicensingIssues,
  excludeSecurityIssues,
  onToggleExcludeSecurityIssues,
  excludeQualityIssues,
  onToggleExcludeQualityIssues,
  isDisabled,
}) => (
  <>
    <Section.Heading>
      Base layer
    </Section.Heading>
    <Section.Paragraph>
      Prevent issues from being created if a dependency is used on the base layer.
      {' '}
      This also prevents issues from dependencies used in both the base layer and other container layers.
    </Section.Paragraph>
    <FormGroup spacing="xs">
      <LabeledCheckbox
        label="Exclude licensing issues"
        isChecked={excludeLicensingIssues}
        isDisabled={isDisabled}
        onChange={(_event, isChecked) => onToggleExcludeLicensingIssues(isChecked)}
        isRippleDisabled
      />
      <LabeledCheckbox
        label="Exclude security issues"
        isChecked={excludeSecurityIssues}
        isDisabled={isDisabled}
        onChange={(_event, isChecked) => onToggleExcludeSecurityIssues(isChecked)}
        isRippleDisabled
      />
      <LabeledCheckbox
        label="Exclude quality issues"
        isChecked={excludeQualityIssues}
        isDisabled={isDisabled}
        onChange={(_event, isChecked) => onToggleExcludeQualityIssues(isChecked)}
        isRippleDisabled
      />
    </FormGroup>
  </>
);
